import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { ValidationError } from "joi";
import { Nullish } from "utility-types";

import { useSelectTheme, useSelectWorkspaces } from "../../store";
import { v, RIF, useForm, useCurrentWorkspaceState } from "../../lib";
import { ContactsIcon } from "../../asset/image";
import { Textarea, LabelBox } from "..";

const CONTACT_WORD_COUNT_MAXIMUM = 250;

export interface ContactBlockPropsT {
  contactValue: string | Nullish;
  onChange: (value: string | undefined) => void;
  onValidated: () => void;
  onError: (error: ValidationError) => void;
  isSummaryReview?: boolean;
}

interface FormData {
  contactDescription?: string;
}

const schema = v.object<FormData>({
  contactDescription: v.string().min(1).max(CONTACT_WORD_COUNT_MAXIMUM).required(),
});

export const ContactBlock = (props: ContactBlockPropsT) => {
  const { pad, color, fontWeight, fontSize } = useSelectTheme();
  const workspacesState = useSelectWorkspaces();
  const { workspaceId } = useCurrentWorkspaceState();
  const role = workspacesState.roleInWorkspaces[workspaceId ?? ""] || "owner";

  const { contactValue, isSummaryReview, onChange, onValidated, onError } = props;

  const { control, watch, reset, getValues } = useForm<FormData>({
    schema,
    shouldFocusError: false,
  });

  useEffect(() => {
    if (contactValue) {
      const { contactDescription } = getValues();
      if (contactValue !== contactDescription) {
        reset({ contactDescription: contactValue });
      }
    }
  }, [contactValue, reset, getValues]);

  const contactDescriptionField = watch("contactDescription");

  useEffect(() => {
    const form: FormData = {
      contactDescription: contactDescriptionField,
    };
    onChange(form.contactDescription);

    const result = schema.validate(form);
    if (!result.error) {
      onValidated();
    } else {
      onError(result.error);
    }
  }, [contactDescriptionField]);

  return (
    <div
      css={{
        display: "flex",
        background: color.white,
        borderRadius: "5px;",
        width: "55vw",
        boxShadow: "0px 4px 12px 0px #D4D4D440",
        marginBottom: pad.large,
      }}
    >
      {/* left */}
      <div
        css={{
          padding: pad.large,
          borderRight: `1px solid ${color.grey_100}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "26%",
        }}
      >
        <div>
          <div
            css={{
              width: "40px",
              height: "40px",
              background: color.background,
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: pad.large,
            }}
          >
            <img src={ContactsIcon} width="24px" alt="" aria-hidden />
          </div>
          <p
            css={{
              fontSize: "18px",
              fontWeight: fontWeight.thick,
              marginBottom: pad.medium,
            }}
          >
            Contact
          </p>
          {RIF(
            !isSummaryReview,
            <p css={{ lineHeight: "19px", fontWeight: fontWeight.regular }}>
              Provide contact information for your participants to get in touch during the project.
            </p>,
          )}
        </div>

        {/* {RIF(!isSummaryReview,
          <div css={{
            width: '100%',
            height: '149px',
            background: color.background,
            borderRadius: '5px',
          }}></div>)} */}
      </div>

      {/* right */}
      <div css={{ padding: pad.xl, width: "74%" }}>
        {RIF(
          !isSummaryReview,
          <>
            <p css={{ fontSize: "14px", fontWeight: fontWeight.medium, marginBottom: pad.xl }}>
              Information for your participants to get in touch:
            </p>
            <Controller
              control={control}
              name="contactDescription"
              render={({ field: { name, value, onChange } }) => (
                <LabelBox
                  {...{
                    label: "Contact Information",
                    required: true,
                    leftCharacters:
                      CONTACT_WORD_COUNT_MAXIMUM - (contactDescriptionField?.length ?? 0),
                  }}
                >
                  <Textarea
                    {...{
                      name,
                      value: value ?? "",
                      onChange,
                    }}
                    maxLength={CONTACT_WORD_COUNT_MAXIMUM}
                    readOnly={role === "viewer"}
                    rows={4}
                    placeholder="Please write contact information for your participants"
                  />
                </LabelBox>
              )}
            />
          </>,
        )}

        {RIF(
          isSummaryReview && contactValue,
          <div css={{ margin: pad.medium }}>{contactValue}</div>,
        )}

        {RIF(
          !isSummaryReview,
          <p
            css={{
              fontSize: fontSize.h7,
              color: color.textIcon.secondary,
              marginBottom: "52px",
              marginTop: pad.xl,
            }}
          >
            Example 1: If you have any questions or encounter issues during the project, please
            contact alix@bestuniversity.edu
          </p>,
        )}
      </div>
    </div>
  );
};
